/**
 * <CommentThread> — per-finding conversation control (Bucket QQ-W2).
 *
 * Mounts inline under each Action Tracker finding card. Shows the
 * thread of comments authored by the buyer, the assessor, and any
 * collaborator-tier recipients on this finding's case.
 *
 * The component runs in two modes:
 *   - "buyer" mode (default): hits the client-portal endpoints. The
 *     authenticated client posts as themselves; the API enforces
 *     case ownership.
 *   - "recipient" mode: passes a grant_id and hits the validate
 *     endpoints. Reads always work; posting fails (422) when the
 *     grant's permission_level is 'viewer' — the UI surfaces the
 *     server message so the recipient understands why.
 *
 * Authorial labeling:
 *   The server returns an `author_label` string and an `author_kind`
 *   enum. The label is a UI-friendly identifier (display_name for
 *   buyer + assessor, match_value for specific-mode collaborator).
 *   For bank-mode + open-mode grants the label is NULL, and we fall
 *   back to a generic "Recipient" tag — the per-event identity
 *   lives in recipient_access_event, not on the comment row.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listFindingCommentsApiV1ClientPortalFindingsEvidenceCardIdCommentsGet,
  postFindingCommentApiV1ClientPortalFindingsEvidenceCardIdCommentsPost,
} from "@hackereyes/api-client";
import "./comment-thread.css";

interface CommentRow {
  id: string;
  evidence_card_id: string;
  case_id: string;
  author_kind: "buyer" | "collaborator" | "assessor";
  author_label: string | null;
  body: string;
  created_at: string;
}

interface CommentThreadProps {
  evidenceCardId: string;
  /** Optional cache-key salt — typically the case_id so each finding's
   *  thread invalidates correctly when posts land. */
  cacheKeyExtra?: string;
}

const KIND_LABEL: Record<CommentRow["author_kind"], string> = {
  buyer: "You",
  assessor: "Assessor",
  collaborator: "Recipient",
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function CommentThread({ evidenceCardId, cacheKeyExtra }: CommentThreadProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  const queryKey = ["finding-comments", evidenceCardId, cacheKeyExtra ?? ""];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } =
        await listFindingCommentsApiV1ClientPortalFindingsEvidenceCardIdCommentsGet({
          path: { evidence_card_id: evidenceCardId },
        });
      if (error) throw new Error("load_failed");
      return (data ?? []) as unknown as CommentRow[];
    },
  });

  const post = useMutation({
    mutationFn: async () => {
      const body = draft.trim();
      if (!body) throw new Error("Comment body is required.");
      const { data, error } =
        await postFindingCommentApiV1ClientPortalFindingsEvidenceCardIdCommentsPost({
          path: { evidence_card_id: evidenceCardId },
          body: { body },
        });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new Error(detail || "Couldn't post comment.");
      }
      return data;
    },
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const comments = data ?? [];
  const count = comments.length;

  return (
    <div className="comment-thread">
      <button
        type="button"
        className="comment-thread-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span aria-hidden="true" className="comment-thread-chev">
          {open ? "▾" : "▸"}
        </span>
        Comments
        <span className="comment-thread-count" aria-label={`${count} comment${count === 1 ? "" : "s"}`}>
          {count}
        </span>
      </button>

      {open && (
        <div className="comment-thread-body">
          {isLoading && (
            <p className="comment-thread-loading">Loading comments…</p>
          )}
          {!isLoading && comments.length === 0 && (
            <p className="comment-thread-empty">
              No comments yet. Post the first message to start a thread with
              the assessor.
            </p>
          )}
          {!isLoading && comments.length > 0 && (
            <ol className="comment-thread-list">
              {comments.map((c) => (
                <li key={c.id} className={`comment-row comment-row-${c.author_kind}`}>
                  <div className="comment-row-head">
                    <span className={`comment-author-badge comment-author-${c.author_kind}`}>
                      {KIND_LABEL[c.author_kind]}
                    </span>
                    {c.author_label && c.author_kind !== "buyer" && (
                      <span className="comment-author-label">{c.author_label}</span>
                    )}
                    <span className="comment-time">{formatTime(c.created_at)}</span>
                  </div>
                  <p className="comment-body">{c.body}</p>
                </li>
              ))}
            </ol>
          )}

          <form
            className="comment-thread-form"
            onSubmit={(e) => {
              e.preventDefault();
              post.mutate();
            }}
          >
            <label
              htmlFor={`comment-input-${evidenceCardId}`}
              className="comment-thread-form-label"
            >
              Add a comment
            </label>
            <textarea
              id={`comment-input-${evidenceCardId}`}
              className="comment-thread-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              placeholder="Question for the assessor, fix-status update, additional context…"
              maxLength={10_000}
            />
            <div className="comment-thread-actions">
              {post.isError && (
                <span className="comment-thread-error">
                  {(post.error as Error).message}
                </span>
              )}
              <button
                type="submit"
                className="comment-thread-submit"
                disabled={!draft.trim() || post.isPending}
              >
                {post.isPending ? "Posting…" : "Post comment"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
